import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import Swal from 'sweetalert2';
import SVG from 'react-inlinesvg';
import { Rating as Stars } from '@mui/material';

import Text from '../../../../shared/Text';
import TextInput from '../../../../shared/FormStyles/TextInput.jsx';
import { Button } from '../../../../shared/Button/Button';
import Close from '../../../../assets/Close.svg';
import { useCreateUserFeedbackMutation } from './hooks/useCampaignUserFeedbackMutation';

/**
 * Post-campaign rating modal. The stars stay editable until the user presses
 * Submit, and a single request carries the final star value and the comment.
 */
const Rating = ({ campaignUuid }) => {
  const theme = useTheme();
  const { t } = useTranslation('campaigns');
  const [starRating, setStarRating] = useState(null);
  const [textRating, setTextRating] = useState('');
  const {
    createUserFeedbackMutation,
    isCreating,
  } = useCreateUserFeedbackMutation({ campaignUuid });

  const starRatingChanged = (event, rate) => {
    // MUI reports null when the selected star is clicked again; keep the current value.
    if (rate === null || isCreating) return;
    setStarRating(rate);
  };

  const textRatingChanged = event => {
    setTextRating(event.target.value);
  };

  const submitFeedback = () => {
    if (!starRating || isCreating) return;
    createUserFeedbackMutation({ rate: starRating, textRating, campaignUuid });
  };

  // Leaving without submitting stores nothing; the modal is offered again next time.
  const dismiss = () => {
    if (isCreating) return;
    Swal.close();
  };

  return (
    <FormWrapper>
      <CloseButton
        type="button"
        aria-label={t('feedback.close')}
        onClick={dismiss}
      >
        <SVG src={Close} />
      </CloseButton>
      <Text variant="h6" m="10px 0 0">
        🎉 {t('feedback.congrats')}
      </Text>
      <Body>
        <Text variant="subtitle2" as="a">
          {t('feedback.rate')}
        </Text>
        <StarsWrapper>
          <Stars
            name="campaign-rating"
            value={starRating ?? 0}
            onChange={starRatingChanged}
            readOnly={isCreating}
            icon={
              <i
                className="fa fa-star"
                style={{ color: theme.colors.primary['100'], fontSize: '32px' }}
              ></i>
            }
            emptyIcon={
              <i
                className="fa fa-star"
                style={{ color: theme.colors.black['20'], fontSize: '32px' }}
              ></i>
            }
          />
        </StarsWrapper>
      </Body>
      {starRating && (
        <CommentSection>
          <Text variant="h6">{t('feedback.tellUsMore')}</Text>
          <TextInput
            name="feedbackMessage"
            as="textarea"
            height="100px"
            width="426px"
            m="10px 0 10px"
            charactersLimit={300}
            value={textRating}
            onChange={textRatingChanged}
          />
          <Button
            mode="contained"
            variant="primary"
            m="15px 0 10px"
            disabled={isCreating}
            onClick={submitFeedback}
          >
            {t('feedback.submit')}
          </Button>
        </CommentSection>
      )}
    </FormWrapper>
  );
};

export default Rating;

const FormWrapper = styled.div`
  width: 100%;
  min-height: 250px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
`;

const StarsWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0 10px;
`;

const Body = styled.div`
  margin-top: 20px;
  width: 100%;
`;

const CommentSection = styled.div`
  width: 100%;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 13px;
  right: 13px;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  line-height: 0;

  svg {
    width: 20px;
    height: 20px;
  }

  path {
    stroke: ${themeGet('colors.black.80')};
  }
`;
